import { DelayRender } from '@droplet-tech-code/core-elements/module/ui/Delay/Delay';
import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { FormHandler } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { SelectInputBase } from '@droplet-tech-code/core-elements/module/ui/Inputs/SelectInput/SelectInput';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { ScrollView, View } from '@droplet-tech-code/core-elements/module/ui/View';
import {
  isNativeMobile,
  isWeb,
  notEmpty,
} from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import {
  BookingClientWithPurchasesAndTickets,
  SupportRequestBackendClient,
} from '@naus-code/naus-admin-types';
import { useEffect, useMemo, useRef } from 'react';

import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { supportRequestApi } from '~/screens/Support/Requests/SupportRequests.api';
import { SquarePill } from '~/screens/Support/Requests/Pending/Flipper-SupportRequestsPending/Flipper.SupportRequestPending.ListItem';
import { translate } from '~/utils/translation.utils';

import { purchaseActionApi } from '../PurchaseActions.api';
import { CancellationMethod } from './Cancel/Cancel.utils';
import { CancelBookingFooter } from './Cancel/CancelBooking.Footer';
import { CancelBookingForm } from './Cancel/CancelBooking.Form';
import { BookingTicketsCheckList } from './Cancel/CancelBooking.TicketSelector';
import {
  bookingHasProtection,
  cancelBookingManager,
  CancelBookingProps,
  getGroupColor,
  getMethodsAcrossPurchases,
  groupPurchasesByProvider,
  ModalActionPanelCancelBookingKey,
  PurchaseAvailableMethods,
} from './Cancel/CancelBooking.utils';

const Header = () => {
  const { closeModal } = useModal();
  return (
    <Modal.Header
      title={translate('purchases.purchaseTool.cancelBookingTitle')}
      divider
      onPress={() => closeModal(ModalActionPanelCancelBookingKey)}
    />
  );
};

export const ActionPanelCancelBooking = (props: CancelBookingProps) => {
  return (
    <Modal.Container>
      <Header />
      <BookingFetcher {...props} />
    </Modal.Container>
  );
};

const BookingFetcher = (props: CancelBookingProps) => {
  const { data: bookingData, isLoading: isLoadingBooking } =
    bookingApi.useGetBookingQuery(props.bookingId);
  const { data: supportData, isLoading: isLoadingSupport } =
    supportRequestApi.useGetSupportRequestQuery(props.supportId);

  if (isLoadingBooking || isLoadingSupport) {
    return (
      <DelayRender isLoading>
        <View />
      </DelayRender>
    );
  }
  if (!bookingData || !supportData) return null;

  return (
    <Body
      {...props}
      booking={bookingData.booking}
      supportRequest={supportData.supportRequest}
    />
  );
};

const Body = ({
  booking,
  supportRequest,
  ...props
}: CancelBookingProps & {
  booking: BookingClientWithPurchasesAndTickets;
  supportRequest: SupportRequestBackendClient;
}) => {
  const formHandlerRef = useRef(new FormHandler());

  // Hide already-cancelled purchases from the list. A purchase is "already
  // cancelled" if it's fully voided (`void`), visually-voided (`voidPseudo`),
  // or both customer- and provider-side voided.
  const cancellablePurchases = useMemo(
    () =>
      booking.purchases.filter(
        (p) => !p.void && !(p.voidCustomerOnly && p.voidProviderOnly),
      ),
    [booking.purchases],
  );

  const [selectedByPurchase, setSelectedByPurchase] =
    cancelBookingManager.state.useGlobalState('selectedTicketsByPurchase');
  const [cancelMode, setCancelMode] = cancelBookingManager.state.useGlobalState('type');

  useEffect(() => {
    const hasProtection = bookingHasProtection(booking);
    const next: { [purchaseId: string]: string[] } = {};
    // CFAR/KOALA covers the whole booking — always select all purchases.
    // Otherwise, if opened from a specific purchase, default to just that one.
    const targets =
      props.defaultPurchaseId && !hasProtection
        ? cancellablePurchases.filter((p) => p._id === props.defaultPurchaseId)
        : cancellablePurchases;
    for (const p of targets) {
      next[p._id] = p.tickets.map((t) => t._id);
    }
    setSelectedByPurchase(next);
    return () => {
      cancelBookingManager.reset();
    };
  }, []);

  // The set of purchases the user actively has at least one ticket selected for
  const activePurchases = useMemo(
    () =>
      cancellablePurchases.filter((p) => (selectedByPurchase[p._id]?.length ?? 0) > 0),
    [cancellablePurchases, selectedByPurchase],
  );

  const providerGroups = useMemo(
    () => groupPurchasesByProvider(activePurchases),
    [activePurchases],
  );

  const allProviderGroups = useMemo(
    () => groupPurchasesByProvider(cancellablePurchases),
    [cancellablePurchases],
  );

  const methodAvailability: PurchaseAvailableMethods = useMemo(
    () => getMethodsAcrossPurchases(activePurchases),
    [activePurchases],
  );

  const remainingOptions = [
    methodAvailability.visual
      ? {
          value: 'visual' as const,
          label: translate('purchases.purchaseTool.visually'),
        }
      : null,
    methodAvailability.customer
      ? {
          value: 'customer' as const,
          label: translate('purchases.purchaseTool.customerOnly'),
        }
      : null,
    methodAvailability.provider
      ? {
          value: 'provider' as const,
          label: translate('purchases.purchaseTool.providerOnly'),
        }
      : null,
    methodAvailability.customer_and_provider
      ? {
          value: 'customer_and_provider' as const,
          label: translate('purchases.purchaseTool.customerProviderOnly'),
        }
      : null,
  ].filter(notEmpty);

  // If current method becomes invalid for current selection, fall back to
  // the most-comprehensive remaining option (matching single-purchase behavior).
  useEffect(() => {
    if (!cancelMode) {
      const last = remainingOptions[remainingOptions.length - 1];
      if (last) setCancelMode(last.value as CancellationMethod);
      return;
    }
    if (remainingOptions.some((o) => o.value === cancelMode)) return;
    const last = remainingOptions[remainingOptions.length - 1];
    if (last) setCancelMode(last.value as CancellationMethod);
  }, [cancelMode, remainingOptions]);

  // Calculation request — once for the whole active selection
  const ticketIds = useMemo(
    () => activePurchases.flatMap((p) => selectedByPurchase[p._id] ?? []),
    [activePurchases, selectedByPurchase],
  );
  const purchaseIds = useMemo(() => activePurchases.map((p) => p._id), [activePurchases]);

  const {
    data: calculation,
    isLoading: isLoadingCalc,
    isFetching: isFetchingCalc,
  } = purchaseActionApi.useGetCancelPurchaseCalculationQuery(
    {
      purchaseIds,
      ticketIds,
    },
    {
      skip: purchaseIds.length === 0 || ticketIds.length === 0,
      refetchOnMountOrArgChange: true,
    },
  );
  const isBookingProtection =
    calculation?.reason === 'CFAR' || calculation?.reason === 'KOALA';

  // Expected refund from company comes from a separate provider-side
  // calculation, only relevant when the cancel involves the provider.
  const isProviderCancel =
    cancelMode === 'provider' || cancelMode === 'customer_and_provider';
  const {
    data: providerCalculation,
    isLoading: isLoadingProviderCalc,
    isFetching: isFetchingProviderCalc,
  } = purchaseActionApi.useGetCancelPurchaseCalculationFromProviderQuery(
    {
      purchaseIds,
      ticketIds,
    },
    {
      skip:
        !isProviderCancel ||
        purchaseIds.length === 0 ||
        ticketIds.length === 0 ||
        !isBookingProtection,
      refetchOnMountOrArgChange: true,
    },
  );

  const protection = bookingHasProtection(booking);
  const noModesAvailable = remainingOptions.length === 0;

  const groupTagsByPurchaseId = useMemo(() => {
    const map: { [purchaseId: string]: { text: string; bg: string; fg: string } } = {};
    for (const g of allProviderGroups) {
      const palette = getGroupColor(g.colorIndex);
      for (const p of g.purchases) {
        map[p._id] = {
          text: g.providerReservationCode,
          bg: palette.bg,
          fg: palette.fg,
        };
      }
    }
    return map;
  }, [allProviderGroups]);

  return (
    <VStack flex={1} style={{ minHeight: 0 }}>
      <HStack
        flex={1}
        align="flex-start"
        style={[isWeb && { height: 600 }, { overflow: 'hidden', minHeight: 0 }]}
      >
        <ScrollView style={{ flex: 1, minHeight: 0, height: '100%' }}>
          <BookingTicketsCheckList
            purchases={cancellablePurchases}
            selectedByPurchase={selectedByPurchase}
            onChangePurchase={(purchaseId, next) => {
              setSelectedByPurchase({
                ...selectedByPurchase,
                [purchaseId]: next,
              });
            }}
            onChangeAll={(next) => setSelectedByPurchase(next)}
            groupTagsByPurchaseId={groupTagsByPurchaseId}
            disabled={!!protection}
          />
        </ScrollView>
        {isWeb ? <Divider vertical /> : null}
        {purchaseIds.length == 0 ? (
          <></>
        ) : (
          <ScrollView style={[{ flex: 1, minHeight: 0, height: '100%' }]}>
            <VStack
              m="3"
              space="3"
              justify="flex-start"
              flex={isNativeMobile ? 1 : undefined}
            >
              <View p="3">
                <SelectInputBase
                  label={translate('purchases.purchaseTool.selectTypeOfCancel')}
                  noFeedback
                  feedback=""
                  type="single-select"
                  values={cancelMode ? [cancelMode] : []}
                  setValues={(nv) => {
                    if (nv) {
                      formHandlerRef.current = new FormHandler();
                      setCancelMode(nv[0] as CancellationMethod);
                    }
                  }}
                  options={remainingOptions}
                />
              </View>

              {noModesAvailable ? (
                <View px="3">
                  <Text.Body2Regular color="warning-mid">
                    {translate('purchases.purchaseTool.mixedCancelStateWarning')}
                  </Text.Body2Regular>
                </View>
              ) : null}

              <DelayRender
                isLoading={
                  isLoadingCalc ||
                  isFetchingCalc ||
                  isLoadingProviderCalc ||
                  isFetchingProviderCalc
                }
              >
                {cancelMode && cancelMode !== 'visual' ? (
                  <CancelBookingForm
                    formHandlerRef={formHandlerRef}
                    method={cancelMode}
                    providerGroups={providerGroups}
                    calculation={calculation}
                    providerCalculation={providerCalculation}
                    supportRequest={supportRequest}
                    purchaseCount={isBookingProtection ? booking.purchases.length : 1}
                  />
                ) : null}
              </DelayRender>

              {protection ? (
                <View align="center">
                  <SquarePill
                    text={translate('purchases.purchaseTool.bookingHasProtection')}
                    bgColor="#FFE9B3"
                    fColor="#A86A00"
                    size="medium"
                  />
                </View>
              ) : null}
            </VStack>
          </ScrollView>
        )}
      </HStack>

      <CancelBookingFooter
        {...props}
        booking={booking}
        supportRequest={supportRequest}
        formHandlerRef={formHandlerRef}
        providerGroups={providerGroups}
        isCalcInFlight={isLoadingCalc || isFetchingCalc}
      />
    </VStack>
  );
};
